/**
 * What is actually wrong with a site, as the map's rows and marker cards name it.
 *
 * A row that reads "1 Critical" tells an operator how bad a site is but not
 * what is wrong with it, so triage still means opening the site. Naming the
 * alarm — "Loss of Power", "Spare 1" — is what lets an operator work the list
 * itself. This module turns the alarm rows the map endpoints carry into
 * ready-to-render chip descriptors: worst-first, named from the
 * alarm-definition catalog so the map reads exactly like the site dashboard,
 * and collapsible behind a "+N" chip where a fixed-height row runs out of room.
 *
 * Contains:
 * - toAlarmChips — alarm rows -> ordered, merged chip descriptors
 * - collapseAlarmChips — visible/hidden split for a row of limited width
 *
 * Returns plain JS — no JSX, no redux.
 */
import { compareSeverity } from '@sophocles/utils/alarmSeverity';

// An alarm arriving without a recognized severity reads as `warning`, the same
// floor severityStatusFamily and getHighestSeverity fall back to.
const FALLBACK_SEVERITY = 'warning';

// Severity -> [i18n key, English fallback], the last rung of the naming ladder:
// what a chip says when nothing identifies the alarm but how bad it is.
const SEVERITY_LABEL = Object.freeze({
  critical: Object.freeze(['alarms.severity.critical', 'Critical']),
  high: Object.freeze(['alarms.severity.high', 'High']),
  warning: Object.freeze(['alarms.severity.warning', 'Warning']),
  attention: Object.freeze(['alarms.severity.attention', 'Attention']),
  info: Object.freeze(['alarms.severity.info', 'Info']),
});

function humanize(key) {
  return String(key ?? '').replace(/_/g, ' ');
}

// An alarm category, out of the category namespace, falling back to the
// definition one for the categories the locales only spell there.
function nameCategory(categoryKey, tAlarm) {
  return tAlarm([`alarms.cat_${categoryKey}`, `alarms.${categoryKey}`], { defaultValue: humanize(categoryKey) });
}

/**
 * Name one alarm, taking the most specific identity the row carries.
 *
 * The ladder runs from the most specific identity to the least: the alarm
 * definition joined from the reference catalog by `alarm_id` (the same join the
 * dashboard's alarm card makes), then the definition the payload spells out
 * itself, then a bare name, then the leaf alarm category, then the top-level
 * category, and finally the severity alone. Each rung reads one wire field, so
 * a renamed field is a one-line change.
 *
 * Alarm definitions and alarm categories live in separate translation
 * namespaces — `alarms.<key>` and `alarms.cat_<key>`. A category key is named
 * out of the category namespace, falling back to the definition one, because
 * the two disagree on a handful of keys and only some categories are spelled
 * in both. The list rows call a leaf category `category_leaf_key` and the
 * marker card calls it `category`; they hold the same key and must name it
 * the same way.
 *
 * @param {object} alarm - one entry of a map row's or detail card's alarms.
 * @param {{ definitionsById: Map, tAlarm: Function, t: Function }} deps
 * @returns {{ key: string, label: string }} chip identity and display text.
 */
function nameAlarm(alarm, { definitionsById, tAlarm, t }) {
  const definition = definitionsById?.get(alarm.alarm_id) ?? null;
  if (definition?.key) {
    const fallback = definition.display_name || definition.key;
    return { key: `alarm:${alarm.alarm_id}`, label: tAlarm(`alarms.${definition.key}`, { defaultValue: fallback }) };
  }
  if (alarm.alarm_key) {
    const fallback = alarm.alarm_name || humanize(alarm.alarm_key);
    return { key: `key:${alarm.alarm_key}`, label: tAlarm(`alarms.${alarm.alarm_key}`, { defaultValue: fallback }) };
  }
  if (alarm.alarm_name) return { key: `name:${alarm.alarm_name}`, label: alarm.alarm_name };
  const leafKey = alarm.category_leaf_key || alarm.category;
  if (leafKey) return { key: `leaf:${leafKey}`, label: nameCategory(leafKey, tAlarm) };
  if (alarm.category_key) return { key: `cat:${alarm.category_key}`, label: nameCategory(alarm.category_key, tAlarm) };
  const severity = SEVERITY_LABEL[alarm.severity] ? alarm.severity : FALLBACK_SEVERITY;
  const [labelKey, fallback] = SEVERITY_LABEL[severity];
  return { key: `severity:${severity}`, label: t(labelKey, fallback) };
}

/**
 * Turn a site's alarms into the chips a map row or marker card renders.
 *
 * Alarms that name the same thing merge into one chip carrying their combined
 * count, so a site alarming twice on one definition reads "Loss of Power x2"
 * rather than twice over. Chips come back worst-first, then alphabetical, so
 * the severity an operator must act on sits at the left edge of the row.
 *
 * @param {object[]} alarms - the endpoint's alarm rows; absent reads as none.
 * @param {{ definitionsById: Map, tAlarm: Function, t: Function }} deps -
 *   `definitionsById` is the reference alarm-definition catalog keyed by id,
 *   `tAlarm` the alarm-namespace translator, `t` the general one.
 * @returns {{ key: string, label: string, severity: string, categoryKey: string, count: number }[]}
 */
export function toAlarmChips(alarms, deps) {
  const byKey = new Map();
  for (const alarm of alarms ?? []) {
    if (!alarm) continue;
    const { key, label } = nameAlarm(alarm, deps);
    const count = alarm.count ?? 1;
    const existing = byKey.get(key);
    if (existing) {
      byKey.set(key, { ...existing, count: existing.count + count });
      continue;
    }
    const severity = alarm.severity ?? deps.definitionsById?.get(alarm.alarm_id)?.severity ?? FALLBACK_SEVERITY;
    const categoryKey = alarm.category_key ?? alarm.category ?? '';
    byKey.set(key, { key, label, severity, categoryKey, count });
  }
  return [...byKey.values()].sort(
    (left, right) => compareSeverity(left.severity, right.severity) || left.label.localeCompare(right.label),
  );
}

/**
 * Split chips into the ones a row shows and the ones a "+N" chip stands for.
 * A set that fits shows whole — the overflow chip only earns its place when it
 * actually hides something.
 *
 * @param {object[]} chips - toAlarmChips output, already worst-first.
 * @param {number} limit - how many chips the surface has room for.
 * @returns {{ visible: object[], hidden: object[] }}
 */
export function collapseAlarmChips(chips, limit) {
  if (chips.length <= limit) return { visible: chips, hidden: [] };
  return { visible: chips.slice(0, limit), hidden: chips.slice(limit) };
}
